import {useTranslation} from "react-i18next";
import {Box, Button, Grid, Typography} from "@mui/material";
import {FilterPanel} from "./components";
import React, {useEffect, useState} from "react";
import {filterInitialState, Plan as PlanType, PlanFilterParams, SearchFunction} from "../../../types/plan";
import Container from "../../../common/Container";
import {useDispatch, useSelector} from "react-redux";
import {State} from "../../../redux/store";
import {Company} from "../../../types/company";
import {DataGrid} from "@mui/x-data-grid";
import NoRowsOverlay from "../../../common/NoRowsOverlay";
import {ActionColumn, Column, columnStyle, DateColumn, DateTimeColumn, GrossColumn} from "../../../common/Columns";
import {AuctionType, PlanItemState, PlanState, ProductType} from "../../../utils/enums";
import {useLocation, useNavigate} from "react-router-dom";
import {useSnack} from "../../../utils/useSnack";
import {planService} from "../../../api/services/plan.service";
import {useConfirmation} from "../../../context/ConfirmationModalProvider";
import {SET_PLAN} from "../../../redux/reducers/plan";
import {formatDate} from "../../../utils/dates";
import BoxWithLabel from "../../../common/BoxWithLabel";

export default function Plan() {
    const {confirm} = useConfirmation();
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {snack} = useSnack();
    const dispatch = useDispatch();

    const pathArray = location.pathname.split('/');
    const planId = +pathArray[pathArray.length - 1];
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [isNewOpen, setIsNewOpen] = useState(false);
    const [planItems, setPlanItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const state = useSelector((state: State) => state);
    const company: Company | null = state.company.company;
    const plan: PlanType | null = state.plan.plan;


    async function getPlanItems(name: string | null, auctionType: AuctionType | null, state: PlanItemState | null,
                                auctionDate: Date | null, productType: ProductType | null) {
        if (!company) return;
        try {
            setLoading(true);
            const response = await planService.getPlanItems(planId, company.id, page, pageSize, name, auctionType, state, auctionDate, productType, i18n.language);
            if (response.status === "success" && response.data) {
                setPlanItems(response.data.data);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    async function getPlan() {
        const response = await planService.getPlan(company!.id, planId)
        if (response.status === "success" && response.data) {
            dispatch({type: SET_PLAN, payload: response.data});
        }
    }

    const [filters, setFilters] = useState<PlanFilterParams>(filterInitialState)
    const search: SearchFunction<PlanFilterParams> = (filters: PlanFilterParams) => {
        setFilters(filters);
    }

    const handleDeletePlan = async (planItemId: number) => {
        const result = await confirm({
            title: t('cabinet.plan.confirm.remove_plan_item.title'),
            message: t('cabinet.plan.confirm.remove_plan_item.body')
        });
        setLoading(true);
        try {
            if (result) {
                const response = await planService.deletePlanItem(company!.id, planId, planItemId)
                if (response.status === "success") {
                    snack(t("snack.success.plan_item_deleted"), "success");
                    await getPlanItems(filters.name, filters.auctionType, filters.state, filters.auctionDate, filters.productType)
                }
            }
        } catch (error) {
            snack(t("snack.error.default"), "error")
        } finally {
            setLoading(false);
        }
    }

    const handleCreate = async () => {
        navigate(`/account-plan/${planId}/plan-item`)
    }

    const handleDelete = async () => {
        const result = await confirm({
            title: t('cabinet.plan.confirm.remove_plan.title'),
            message: t('cabinet.plan.confirm.remove_plan.body')
        });
        try {
            if (result) {
                const response = await planService.deletePlan(company!.id, planId)
                if (response.status === "success") {
                    snack(t("snack.success.plan_deleted"), "success");
                    navigate(`/account-plans`)
                }
            } else {
                console.log("Удаление отменено!");
            }
        } catch (error) {
            snack(t("snack.error.default"), "error")
        } finally {
            setLoading(false);
        }
    }

    const handleExport = async () => {
        snack(t("snack.warning.function_in_work"), "warning");
    }

    useEffect(() => {
        if (company && planId) getPlan();
    }, [company, planId]);

    useEffect(() => {
        if (company) getPlanItems(filters.name, filters.auctionType, filters.state, filters.auctionDate, filters.productType);
    }, [company, filters, location.pathname, page, pageSize, i18n.language]);

    const actions = [
        {
            label: "action.navigate",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                navigate(`/account-plan/${planId}/plan-item/${row.id}`);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return true;
            }
        },
        {
            label: "action.remove",
            callback: (row: any) => {
                // Логика удаления плана. В данном примере используется подтверждение удаления.
                handleDeletePlan(row.id);
            },
            predicate: (row: any) => {
                // Здесь можно задать условие доступности действия для конкретной записи.
                // Например, вернуть true, если удаление разрешено для данного плана.
                return row.state === PlanItemState.draft;
            }
        }]

    const HeaderSuffix = () => {
        if (i18n.language === "ru") {
            return <span> {t("cabinet.plans.title")} № {plan?.id} {t('common.from')} {formatDate(plan?.createdAt, 'date')} {t('common.year')}</span>

        }
        return <span>{t("cabinet.plans.title")} № {plan?.id} {formatDate(plan?.createdAt, 'date')} {t('common.year')} {t('common.from')}</span>
    }

    function onClose() {
        navigate(`/account-plans`)
    }

    return <Box>
        <Container paddingY={{xs: 2, sm: 4}} maxWidth="unset">
            <Box marginBottom={4} sx={{
                display: "flex",
                flexDirection: {xs: "column", md: "row"},
                justifyContent: {xs: "center", md: "space-between"},
                alignItems: {xs: "flex-start", md: "center"},
                gap: 2,
            }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                    }}
                >
                    <HeaderSuffix/>
                </Typography>

                <Button variant="contained" size="small"
                        onClick={onClose} color="secondary"
                >
                    {t("common.return_back")}
                </Button>
            </Box>


            <Grid item xs={12} sm={7} md={8} lg={9}>
                <BoxWithLabel label={t("common.filters")}>
                    <FilterPanel
                        onSearch={search}
                        t={t}
                        i18n={i18n}
                        initialState={filterInitialState}
                    />
                </BoxWithLabel>
            </Grid>


            <Grid item container spacing={2} alignItems={"center"} mb={2}>
                <Grid item xs={12} sm={5} md={4} lg={3}>
                    <Button fullWidth color="primary" variant="contained" disabled={plan?.state !== PlanState.draft}
                            onClick={handleCreate}>
							<span style={{whiteSpace: 'nowrap'}}>
								{t("cabinet.plan.create")}
							</span>
                    </Button>
                </Grid>
                <Grid item xs={12} sm={5} md={4} lg={3}>
                    <Button fullWidth color="primary" variant="contained"
                            onClick={handleExport}>
							<span style={{whiteSpace: 'nowrap'}}>
								{t("cabinet.plan.export")}
							</span>
                    </Button>
                </Grid>
            </Grid>

            <Box>
                <DataGrid
                    components={{NoRowsOverlay: () => <NoRowsOverlay/>}}
                    sx={{
                        height: 500,
                        "& .MuiDataGrid-cell": {
                            whiteSpace: "pre-wrap"
                        },
                    }}
                    columnBuffer={4}
                    rows={planItems.length
                        ? planItems
                        : []
                    }
                    columns={[
                        ActionColumn(100, columnStyle, "cabinet.plans", actions),
                        Column("planNo", 50, columnStyle, undefined, "cabinet.plan"),
                        Column("productName", 350, columnStyle, undefined, "cabinet.plan"),
                        Column("productType", 350, columnStyle, 'product_type', "cabinet.plan"),
                        DateTimeColumn("createdAt", 200, columnStyle, "cabinet.plan"),
                        Column("auctionType", 100, columnStyle, 'auction_type', "cabinet.plan"),
                        Column("amount", 100, columnStyle, undefined, "cabinet.plan"),
                        GrossColumn(150, columnStyle, "cabinet.plan"),
                        Column("state", 200, columnStyle, "plan_state", "cabinet.plan")
                    ]}
                    loading={loading}
                    density="comfortable" style={{whiteSpace: "pre-wrap"}}
                />
            </Box>
        </Container>

        <Grid item container spacing={2} justifyContent="center" sx={{pb: 3}}>
            <Grid item xs="auto">

            </Grid>
        </Grid>
    </Box>
}